<script lang="ts">
	// The design system's Sheet (design/components/surfaces/Sheet.jsx): a task
	// that sits above the screen it belongs to. One sheet is one scrim and one
	// panel; a stack is two Sheets, level 1 and level 2, drawn by the caller.
	//
	// Every way out does the same thing at a level, so a hand cannot pick the
	// wrong exit: on level 1 the close, Esc and a tap on the scrim call
	// onClose; on level 2 the Back button, Esc and the scrim call onBack.
	// Closing never discards what was typed; the caller keeps the draft.
	// `locked` holds every exit while a request is in flight, because closing
	// mid-charge would leave the cashier not knowing whether it went.
	//
	// The header names the task and does not act on it. From the inline
	// start: the title (on level 2, the parent's name as the Back button, a
	// slash, then the title), then at the inline end one figure the task
	// changes and the close.
	//
	// The actions sit in a row at the bottom, pinned while the body scrolls:
	// an optional quiet secondary at the inline start, the primary at the
	// inline end. The primary is where the task ends, after the fields it
	// depends on, and the close stands alone as the way out.
	//
	// Motion is the design's keyframes by name; the exit plays for --dur-exit
	// before the sheet unmounts.
	//
	// The app adds test ids, and takes the close and back glyphs from Icon
	// rather than copying them, since a Svelte component may import another.
	//
	// Every option below is opt-in, so a Sheet that names none of them is the
	// till's and the dashboard's, as before (#959, #955):
	// - placement "bottom" rises from the screen's foot, full width, at most
	//   --sheet-bottom-max high and as high as its content; "auto" is bottom
	//   under 640px and centred from there. A bottom sheet with no actions
	//   has its close across its foot, under the thumb, and no ×. With
	//   `handle` it also has a pill at its top that closes it on a tap and
	//   never drags (UX-3). Level 2 always sits centred on its parent.
	// - fit: a centred panel is as high as its content, up to the screen less
	//   its insets, rather than pinned top and bottom.
	// - labelledBy: the dialog is named by an element in its body, such as an
	//   item's name heading the item, and the header may then hold no title.
	// - titleLines 2: a long title wraps to a second line before it is cut.
	// - history: while open, the sheet holds one entry in the browser's
	//   history, so the phone's Back closes it; every other close takes the
	//   entry back off (sheetHistory.ts).

	import type { Snippet } from 'svelte';
	import Icon from './Icon.svelte';
	import { pressable } from './pressable';
	import { holdBack } from './sheetHistory';

	interface Props {
		open: boolean;
		title: string;
		level?: 1 | 2;
		/** A level-2 sheet is open on this one: nothing here can be reached. */
		covered?: boolean;
		/** Positions against the nearest positioned ancestor instead of the viewport. */
		contain?: boolean;
		backLabel?: string;
		onBack?: () => void;
		onClose?: () => void;
		closeLabel?: string;
		/** One figure the task changes, such as the delivery fee. Left to right. */
		meta?: string;
		metaLabel?: string;
		primaryLabel?: string;
		/** A check before the label when the primary confirms the task. Never
		 * on a primary that only moves on, such as Next sale. Hidden while
		 * pending, when the words say what is happening. */
		primaryIcon?: 'check';
		onPrimary?: () => void;
		primaryDisabled?: boolean;
		primaryState?: 'idle' | 'pending';
		/** A quiet action at the inline start of the bottom row, such as Remove item. */
		secondaryLabel?: string;
		onSecondary?: () => void;
		secondaryDisabled?: boolean;
		secondaryTestId?: string;
		pendingLabel?: string;
		locked?: boolean;
		testId?: string;
		closeTestId?: string;
		backTestId?: string;
		primaryTestId?: string;
		metaTestId?: string;
		titleTestId?: string;
		/** Where the panel sits: centred (the default), at the foot, or at the foot under 640px. */
		placement?: 'centre' | 'bottom' | 'auto';
		/** A centred panel as high as its content, up to the screen less its insets. */
		fit?: boolean;
		/** The id of the element that names the dialog, in place of the title. */
		labelledBy?: string;
		/** How many lines a long title takes before it is cut. */
		titleLines?: 1 | 2;
		/** A bottom sheet's pill at its top, which closes it on a tap and never drags. */
		handle?: boolean;
		handleTestId?: string;
		/** The phone's Back closes the sheet while it is open. */
		history?: boolean;
		children: Snippet;
	}

	let {
		open,
		title,
		level = 1,
		covered = false,
		contain = false,
		backLabel,
		onBack,
		onClose,
		closeLabel = 'Close',
		meta,
		metaLabel,
		primaryLabel,
		primaryIcon,
		onPrimary,
		primaryDisabled = false,
		primaryState = 'idle',
		secondaryLabel,
		onSecondary,
		secondaryDisabled = false,
		secondaryTestId,
		pendingLabel,
		locked = false,
		testId,
		closeTestId,
		backTestId,
		primaryTestId,
		metaTestId,
		titleTestId,
		placement = 'centre',
		fit = false,
		labelledBy,
		titleLines = 1,
		handle = false,
		handleTestId,
		history = false,
		children,
	}: Props = $props();

	// = --dur-exit: unmount after the sink has played.
	const EXIT_MS = 120;

	let mounted = $state(false);
	let leaving = $state(false);
	let panel = $state<HTMLDivElement>();
	let rtl = $state(true);

	const nested = $derived(level >= 2);
	// The phone width: under 640px, spelled as NavMenu spells it. Read in the
	// browser only, since the catalogue's worker renders this component where
	// there is no window; a closed sheet draws nothing there in any case.
	let phone = $state(false);
	$effect(() => {
		if (placement !== 'auto') return;
		const query = window.matchMedia('(max-width: 639.98px)');
		phone = query.matches;
		const onChange = () => (phone = query.matches);
		query.addEventListener('change', onChange);
		return () => query.removeEventListener('change', onChange);
	});
	const bottom = $derived(!nested && (placement === 'bottom' || (placement === 'auto' && phone)));
	const actions = $derived(!!(primaryLabel || secondaryLabel));
	// A bottom sheet with no actions closes from its foot instead of its header.
	const footClose = $derived(bottom && !actions && !!onClose);
	// A level-1 sheet with steps passes onBack: Back shows in the header and
	// goes one step back, while the close, Esc and the scrim still close the
	// whole task.
	const stepped = $derived(!nested && !!onBack);
	const leave = $derived(locked ? undefined : nested ? onBack : onClose);
	const pending = $derived(primaryState === 'pending');

	$effect(() => {
		if (open) {
			mounted = true;
			leaving = false;
			return;
		}
		if (!mounted) return;
		leaving = true;
		// The panel's sink ending is the signal; the timer is the net under it,
		// for a page whose animations do not run.
		const timer = setTimeout(finishLeaving, EXIT_MS * 2);
		return () => clearTimeout(timer);
	});

	function finishLeaving() {
		if (open || !leaving) return;
		mounted = false;
		leaving = false;
	}

	// The phone's Back, only for a sheet that asks for it. The entry is held
	// while the sheet is open and released on any close; Back takes it itself.
	// The entry is held only while the sheet can close: a locked sheet keeps
	// every exit, and Back with it, until its request answers.
	$effect(() => {
		if (!history || !open || nested || locked) return;
		return holdBack(() => leave?.());
	});

	// Focus moves in on open and back to whatever opened the sheet on close.
	$effect(() => {
		if (!mounted || !panel) return;
		const opener = document.activeElement as HTMLElement | null;
		rtl = getComputedStyle(panel).direction === 'rtl';
		panel.focus({ preventScroll: true });
		return () => {
			if (opener && document.contains(opener)) opener.focus({ preventScroll: true });
		};
	});

	$effect(() => {
		if (!mounted || covered || leaving) return;
		const onKey = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				event.stopPropagation();
				leave?.();
				return;
			}
			if (event.key !== 'Tab' || !panel) return;
			const focusable = panel.querySelectorAll<HTMLElement>(
				'button:not(:disabled),input:not(:disabled),select,textarea,a[href],[tabindex]:not([tabindex="-1"])',
			);
			const first = focusable[0];
			const last = focusable[focusable.length - 1];
			if (!first || !last) return;
			if (
				event.shiftKey &&
				(document.activeElement === first || document.activeElement === panel)
			) {
				event.preventDefault();
				last.focus();
			} else if (!event.shiftKey && document.activeElement === last) {
				event.preventDefault();
				first.focus();
			}
		};
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
	});
</script>

{#if mounted}
	<div
		class="scrim"
		class:contain
		class:leaving
		style:z-index="calc(var(--z-sheet) + {(level - 1) * 2})"
		aria-hidden="true"
		onclick={covered || leaving ? undefined : leave}
	></div>
	<div
		bind:this={panel}
		class="panel"
		class:contain
		class:nested
		class:leaving
		class:bottom
		class:fit={fit && !bottom}
		style:z-index="calc(var(--z-sheet) + {(level - 1) * 2 + 1})"
		role="dialog"
		aria-modal="true"
		aria-label={labelledBy ? undefined : title}
		aria-labelledby={labelledBy}
		aria-hidden={covered ? 'true' : undefined}
		tabindex="-1"
		inert={covered}
		data-sheet-level={level}
		data-testid={testId}
		onanimationend={(event) => {
			if (event.target === event.currentTarget) finishLeaving();
		}}
	>
		{#if bottom && handle && onClose}
			<button
				type="button"
				class="handle"
				data-press="bare"
				data-testid={handleTestId}
				disabled={locked}
				aria-label={closeLabel}
				title={closeLabel}
				onclick={leave}
			>
				<span aria-hidden="true"></span>
			</button>
		{/if}
		{#if title || nested || stepped || meta != null || (!nested && onClose && !footClose)}
			<div class="head">
				{#if nested || stepped}
					<button
						type="button"
						class="control back"
						data-press
						data-testid={backTestId}
						disabled={locked}
						onclick={nested ? leave : onBack}
						{@attach pressable}
					>
						<Icon name="back" size={22} mirror={!rtl} />
						<span class="backLabel">{backLabel}</span>
					</button>
					<span class="slash" aria-hidden="true">/</span>
				{/if}
				{#if title}
					<h2 class:wrap={titleLines === 2 || bottom} data-testid={titleTestId}>{title}</h2>
				{/if}
				<span class="grow"></span>
				{#if meta != null}
					<span class="meta">
						{#if metaLabel}<span class="metaLabel">{metaLabel}</span>{/if}
						<span class="metaValue" dir="ltr" data-testid={metaTestId}>{meta}</span>
					</span>
				{/if}
				{#if !nested && onClose && !footClose}
					<button
						type="button"
						class="control close"
						data-press
						data-testid={closeTestId}
						disabled={locked}
						aria-label={closeLabel}
						title={closeLabel}
						onclick={leave}
						{@attach pressable}
					>
						<Icon name="close" size={22} />
					</button>
				{/if}
			</div>
		{/if}
		<!-- A body that outgrows the panel scrolls inside it and never spills
		     onto the scrim. Callers still size to fit. -->
		<div class="body" data-scroll="">
			{@render children()}
		</div>
		{#if footClose}
			<div class="foot">
				<button
					type="button"
					class="control footClose"
					data-press
					data-testid={closeTestId}
					disabled={locked}
					onclick={leave}
					{@attach pressable}
				>
					{closeLabel}
				</button>
			</div>
		{/if}
		{#if actions}
			<div class="actions">
				{#if secondaryLabel}
					<button
						type="button"
						class="control secondary"
						data-press
						data-testid={secondaryTestId}
						disabled={secondaryDisabled || locked || pending}
						onclick={onSecondary}
						{@attach pressable}
					>
						{secondaryLabel}
					</button>
				{/if}
				<span class="grow"></span>
				{#if primaryLabel}
					<button
						type="button"
						class="control primary"
						data-press="lift"
						data-ground="speed"
						data-testid={primaryTestId}
						disabled={primaryDisabled}
						aria-busy={pending ? 'true' : undefined}
						aria-disabled={pending ? 'true' : undefined}
						onclick={pending ? undefined : onPrimary}
						{@attach pressable}
					>
						{#if primaryIcon === 'check' && !pending}
							<Icon name="check" size={22} />
						{/if}
						{pending && pendingLabel ? pendingLabel : primaryLabel}
					</button>
				{/if}
			</div>
		{/if}
	</div>
{/if}

<style>
	.scrim {
		position: fixed;
		inset: 0;
		background: var(--scrim);
		animation: bosla-fade-in var(--dur-enter) var(--ease-out) both;
	}

	/* On its way out, nothing on it answers a tap: the screen beneath is
	   already the one in charge. */
	.scrim.leaving {
		animation: bosla-fade-out var(--dur-exit) var(--ease-in) both;
		pointer-events: none;
	}

	.panel {
		--inline: max(var(--sheet-inset), calc((100% - var(--sheet-max-w)) / 2));
		--block: max(var(--sheet-inset), calc((100% - var(--sheet-max-h)) / 2));
		position: fixed;
		inset-inline: var(--inline);
		inset-block-start: var(--block);
		inset-block-end: var(--block);
		display: flex;
		flex-direction: column;
		gap: var(--space-7);
		padding: var(--space-7);
		background: var(--surface-card);
		color: var(--text-strong);
		border: var(--border-width) solid var(--border-default);
		border-radius: var(--radius-panel);
		outline: none;
		animation: bosla-rise var(--dur-enter) var(--ease-out) both;
	}

	.contain {
		position: absolute;
	}

	/* fit: centred on the screen and as high as its content. */
	.panel.fit {
		inset-block-start: 50%;
		inset-block-end: auto;
		translate: 0 -50%;
		max-block-size: calc(100% - 2 * var(--sheet-inset));
	}

	.panel.fit .body {
		flex: 0 1 auto;
	}

	/* bottom: across the screen's foot, its top corners rounded, as high as
	   its content up to --sheet-bottom-max. Its parts pad themselves, so the
	   body scrolls between the pinned top and the pinned foot. */
	.panel.bottom {
		inset-inline: 0;
		inset-block-start: auto;
		inset-block-end: 0;
		max-block-size: var(--sheet-bottom-max);
		gap: 0;
		padding: 0;
		border-block-end: 0;
		border-radius: 0;
		border-start-start-radius: var(--radius-panel-lg);
		border-start-end-radius: var(--radius-panel-lg);
	}

	.panel.bottom .head {
		padding: var(--space-7) var(--space-7) var(--space-5);
	}

	.panel.bottom .handle + .head {
		padding-block-start: 0;
	}

	.panel.bottom .body {
		flex: 0 1 auto;
		overscroll-behavior: contain;
		padding: 0 var(--space-7) var(--space-7);
	}

	.panel.bottom > .body:first-child {
		padding-block-start: var(--space-7);
	}

	.panel.bottom .actions {
		margin: 0;
		padding: var(--space-5) var(--space-7);
	}

	.handle {
		flex: none;
		display: flex;
		align-items: center;
		justify-content: center;
		inline-size: 100%;
		block-size: var(--tap-min);
		padding: 0;
		border: 0;
		background: transparent;
		cursor: pointer;
	}

	.handle > span {
		display: block;
		inline-size: var(--space-12);
		block-size: var(--space-1);
		border-radius: var(--radius-pill);
		background: var(--text-muted);
	}

	.foot {
		flex: none;
		display: flex;
		padding: var(--space-5) var(--space-7);
		border-block-start: var(--border-width) solid var(--border-inset);
		background: var(--surface-card);
	}

	/* Button.jsx's secondary at size md, across the foot. */
	.footClose {
		flex: 1 1 auto;
		padding: 14px 20px;
	}

	.panel.nested {
		inset-inline: calc(var(--inline) + var(--sheet-nest));
		inset-block-start: calc(var(--block) + var(--sheet-step));
	}

	.panel.leaving {
		animation: bosla-sink var(--dur-exit) var(--ease-in) both;
		pointer-events: none;
	}

	.head {
		flex: none;
		display: flex;
		align-items: center;
		gap: var(--space-4);
		flex-wrap: wrap;
	}

	h2 {
		margin: 0;
		font-size: var(--text-h4);
		font-weight: 700;
		min-inline-size: 0;
		flex: 0 1 auto;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	h2.wrap {
		white-space: normal;
		display: -webkit-box;
		-webkit-box-orient: vertical;
		-webkit-line-clamp: 2;
		line-clamp: 2;
		line-height: var(--leading-headline);
		overflow-wrap: anywhere;
	}

	.grow {
		flex: 1 0 0;
	}

	.slash {
		font-size: var(--text-h4);
		color: var(--text-muted);
	}

	.meta {
		display: flex;
		align-items: baseline;
		gap: var(--space-3);
	}

	.metaLabel {
		font-size: var(--text-sm);
		color: var(--text-muted);
	}

	.metaValue {
		font-family: var(--font-mono);
		font-size: var(--text-md);
		font-weight: 700;
	}

	/* Button.jsx's secondary, and its primary at size md, as the design copies them. */
	.control {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		gap: 10px;
		min-block-size: var(--ctl-min);
		min-inline-size: var(--ctl-min);
		padding: 0 14px;
		font-family: var(--font-sans);
		font-size: 16px;
		font-weight: 600;
		white-space: nowrap;
		background: var(--surface-card);
		color: var(--text-strong);
		border: var(--border-width) solid var(--border-default);
		border-radius: var(--radius-btn);
		cursor: pointer;
	}

	.primary {
		padding: 14px 20px;
		border: none;
		background: var(--speed);
		color: var(--on-speed);
		box-shadow: var(--shadow-press);
	}

	.close {
		padding: 0;
	}

	/* The bottom row: a hairline above it, and pulled up so the gap above the
	   rule is the row's own, not the panel's larger one. */
	.actions {
		flex: none;
		display: flex;
		align-items: center;
		gap: var(--space-4);
		padding-block-start: var(--space-5);
		margin-block-start: calc(var(--space-7) * -1 + var(--space-5));
		border-block-start: var(--border-width) solid var(--border-inset);
	}

	.secondary {
		color: var(--text-body);
	}

	.back {
		flex: 0 1 auto;
		min-inline-size: 0;
	}

	.backLabel {
		min-inline-size: 0;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	.body {
		flex: 1;
		min-block-size: 0;
		display: flex;
		flex-direction: column;
		overflow-y: auto;
		overflow-x: hidden;
	}
</style>
